import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { templatesAPI } from '../services/api.jsx';

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: '6px',
  fontSize: '13px'
};

const buttonStyle = {
  padding: '8px 12px',
  borderRadius: '6px',
  border: '1px solid #cbd5e1',
  background: '#f8fafc',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 600
};

const defaultTransform = {
  mode: 'contain',
  targetWidth: 600,
  targetHeight: '',
  position: 'center',
  outputFormat: 'png'
};

function CropModal({ image, onClose, onApply }) {
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [crop, setCrop] = useState({ x: 40, y: 30, width: 240, height: 140 });
  const [dragging, setDragging] = useState(false);
  const scaleX = image?.Width && displaySize.width ? image.Width / displaySize.width : 1;
  const scaleY = image?.Height && displaySize.height ? image.Height / displaySize.height : 1;

  const onMouseDown = () => setDragging(true);
  const onMouseUp = () => setDragging(false);
  const onMouseMove = (event) => {
    if (!dragging) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const nextX = Math.max(0, Math.min(event.clientX - rect.left - (crop.width / 2), rect.width - crop.width));
    const nextY = Math.max(0, Math.min(event.clientY - rect.top - (crop.height / 2), rect.height - crop.height));
    setCrop((prev) => ({ ...prev, x: nextX, y: nextY }));
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}>
      <div style={{ width: 'min(960px, 92vw)', background: '#fff', borderRadius: '12px', boxShadow: '0 24px 60px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '18px' }}>Crop Image</h2>
          <button type="button" onClick={onClose} style={{ ...buttonStyle, border: 'none', background: 'transparent', fontSize: '22px' }}>×</button>
        </div>
        <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1fr 280px', gap: '20px' }}>
          <div>
            <div
              style={{ position: 'relative', background: '#0f172a', borderRadius: '10px', overflow: 'hidden', minHeight: '420px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
            >
              <img
                src={image?.OriginalUrl}
                alt={image?.Name}
                style={{ maxWidth: '100%', maxHeight: '420px', display: 'block' }}
                onLoad={(event) => {
                  setDisplaySize({ width: event.currentTarget.clientWidth, height: event.currentTarget.clientHeight });
                }}
              />
              <div
                onMouseDown={onMouseDown}
                style={{
                  position: 'absolute',
                  left: `${crop.x}px`,
                  top: `${crop.y}px`,
                  width: `${crop.width}px`,
                  height: `${crop.height}px`,
                  border: '2px solid #38bdf8',
                  background: 'rgba(56, 189, 248, 0.15)',
                  cursor: 'move',
                  boxShadow: '0 0 0 9999px rgba(15,23,42,0.4)'
                }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gap: '12px', alignContent: 'start' }}>
            <label>
              <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>Crop Width</div>
              <input value={crop.width} onChange={(e) => setCrop((prev) => ({ ...prev, width: Number(e.target.value) || 1 }))} style={inputStyle} />
            </label>
            <label>
              <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>Crop Height</div>
              <input value={crop.height} onChange={(e) => setCrop((prev) => ({ ...prev, height: Number(e.target.value) || 1 }))} style={inputStyle} />
            </label>
            <label>
              <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>Preview Width</div>
              <input value={Math.round(crop.width * scaleX)} readOnly style={{ ...inputStyle, background: '#f8fafc' }} />
            </label>
            <label>
              <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>Preview Height</div>
              <input value={Math.round(crop.height * scaleY)} readOnly style={{ ...inputStyle, background: '#f8fafc' }} />
            </label>
            <button
              type="button"
              onClick={() => onApply({
                cropX: Math.round(crop.x * scaleX),
                cropY: Math.round(crop.y * scaleY),
                cropWidth: Math.round(crop.width * scaleX),
                cropHeight: Math.round(crop.height * scaleY)
              })}
              style={{ ...buttonStyle, background: '#1d4ed8', borderColor: '#1d4ed8', color: '#fff' }}
            >
              Apply Crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplateImages({ embedded = false, onSelectImage = null }) {
  const { hasPermission, hasAnyPermission } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [variants, setVariants] = useState([]);
  const [variantsByImageId, setVariantsByImageId] = useState({});
  const [variantUrl, setVariantUrl] = useState('');
  const [transform, setTransform] = useState(defaultTransform);
  const [error, setError] = useState('');
  const [busyImageId, setBusyImageId] = useState(null);
  const [cropTarget, setCropTarget] = useState(null);

  const selectedImage = useMemo(
    () => images.find((image) => Number(image.ImageId) === Number(selectedImageId)) || null,
    [images, selectedImageId]
  );

  const canUploadImages = hasPermission('template-images:create');
  const canEditImages = hasAnyPermission(['template-images:update:own', 'template-images:update:any']);
  const canDeleteImages = hasAnyPermission(['template-images:delete:own', 'template-images:delete:any']);

  const libraryItems = useMemo(() => {
    const nextItems = [];

    for (const image of images) {
      nextItems.push({
        type: 'original',
        key: `orig-${image.ImageId}`,
        image
      });

      const imageVariants = variantsByImageId[image.ImageId] || [];
      for (const variant of imageVariants) {
        nextItems.push({
          type: 'variant',
          key: `var-${variant.VariantId}`,
          image,
          variant
        });
      }
    }

    return nextItems;
  }, [images, variantsByImageId]);

  const loadImages = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await templatesAPI.listDesignImages();
      const nextImages = response.data?.images || [];
      setImages(nextImages);
      if (!selectedImageId && nextImages.length > 0) {
        setSelectedImageId(nextImages[0].ImageId);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  const loadVariants = async (imageId) => {
    if (!imageId) {
      setVariants([]);
      return;
    }

    try {
      const response = await templatesAPI.listDesignImageVariants(imageId);
      const nextVariants = response.data?.variants || [];
      setVariants(nextVariants);
      setVariantsByImageId((prev) => ({
        ...prev,
        [imageId]: nextVariants
      }));
    } catch {
      setVariants([]);
      setVariantsByImageId((prev) => ({
        ...prev,
        [imageId]: []
      }));
    }
  };

  useEffect(() => {
    loadImages();
  }, []);

  useEffect(() => {
    loadVariants(selectedImageId);
  }, [selectedImageId]);

  const handleUpload = async (event) => {
    if (!canUploadImages) return;

    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setError('');
      const response = await templatesAPI.uploadDesignImage(file);
      const image = response.data?.image;
      if (image) {
        setImages((prev) => [image, ...prev]);
        setSelectedImageId(image.ImageId);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleRename = async (imageId, name) => {
    if (!canEditImages) return;

    try {
      setBusyImageId(imageId);
      const response = await templatesAPI.updateDesignImage(imageId, { name });
      const updated = response.data?.image;
      if (updated) {
        setImages((prev) => prev.map((image) => image.ImageId === imageId ? updated : image));
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to rename image');
    } finally {
      setBusyImageId(null);
    }
  };

  const handleDelete = async (image) => {
    if (!canDeleteImages) return;

    const usageCount = Number(image?.UsageCount || 0);
    const message = usageCount > 0
      ? `This image is used ${usageCount} time(s) in saved templates and cannot be deleted until those references are removed.`
      : 'Delete this template image and all generated variants?';

    if (usageCount > 0) {
      window.alert(message);
      return;
    }

    if (!window.confirm(message)) return;

    try {
      setBusyImageId(image.ImageId);
      await templatesAPI.deleteDesignImageById(image.ImageId);
      setImages((prev) => prev.filter((item) => item.ImageId !== image.ImageId));
      setVariantsByImageId((prev) => {
        const next = { ...prev };
        delete next[image.ImageId];
        return next;
      });
      if (Number(selectedImageId) === Number(image.ImageId)) {
        setSelectedImageId(null);
        setVariantUrl('');
      }
    } catch (err) {
      const apiMessage = err.response?.data?.error || err.message || 'Failed to delete image';
      const usage = err.response?.data?.usageCount;
      setError(usage ? `${apiMessage} (${usage} usage${usage === 1 ? '' : 's'})` : apiMessage);
    } finally {
      setBusyImageId(null);
    }
  };

  const handleTransform = async (overrides = {}) => {
    if (!selectedImage || !canEditImages) return;

    try {
      setBusyImageId(selectedImage.ImageId);
      setError('');
      const payload = {
        ...transform,
        ...overrides,
        targetWidth: transform.targetWidth || null,
        targetHeight: transform.targetHeight || null
      };
      const response = await templatesAPI.transformDesignImage(selectedImage.ImageId, payload);
      const nextVariantUrl = response.data?.variant?.VariantUrl || '';
      setVariantUrl(nextVariantUrl);
      await loadVariants(selectedImage.ImageId);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to transform image');
    } finally {
      setBusyImageId(null);
      setCropTarget(null);
    }
  };

  const shellStyle = embedded
    ? { padding: 0, background: 'transparent', minHeight: 'auto' }
    : { padding: '24px', background: '#f8fafc', minHeight: '100vh' };

  return (
    <div style={shellStyle}>
      {!embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '28px', color: '#0f172a' }}>Template Images</h1>
            <p style={{ margin: '6px 0 0', color: '#475569' }}>Manage CDN-backed template assets for Unlayer designs.</p>
          </div>
          {canUploadImages && (
            <label style={{ ...buttonStyle, background: '#dbeafe', borderColor: '#93c5fd', color: '#1d4ed8' }}>
              {uploading ? 'Uploading...' : 'Upload Image'}
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
            </label>
          )}
        </div>
      )}

      {embedded && canUploadImages && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <label style={{ ...buttonStyle, background: '#dbeafe', borderColor: '#93c5fd', color: '#1d4ed8' }}>
            {uploading ? 'Uploading...' : 'Upload Image'}
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
          </label>
        </div>
      )}

      {error && (
        <div style={{ marginBottom: '16px', padding: '12px 14px', borderRadius: '8px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: embedded ? '1fr 1fr' : '1.15fr 0.85fr', gap: '20px' }}>
        <div style={{ background: '#fff', border: '1px solid #dbe3ef', borderRadius: '10px', padding: '16px' }}>
          <h2 style={{ marginTop: 0, fontSize: '18px' }}>Library</h2>
          {loading ? (
            <p>Loading images...</p>
          ) : libraryItems.length === 0 ? (
            <p>No template images uploaded yet.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px' }}>
              {libraryItems.map((item) => (
                <div
                  key={item.key}
                  style={{
                    border: item.type === 'variant'
                      ? (variantUrl && variantUrl === item.variant.VariantUrl ? '2px solid #0f766e' : '1px solid #dbe3ef')
                      : (Number(selectedImageId) === Number(item.image.ImageId) && !variantUrl ? '2px solid #3b82f6' : '1px solid #dbe3ef'),
                    borderRadius: '10px',
                    padding: '10px',
                    background: '#fff',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    setSelectedImageId(item.image.ImageId);
                    setVariantUrl(item.type === 'variant' ? item.variant.VariantUrl : '');
                  }}
                >
                  <img
                    src={item.type === 'variant' ? item.variant.VariantUrl : item.image.OriginalUrl}
                    alt={item.type === 'variant' ? `${item.image.Name} variant` : item.image.Name}
                    style={{ width: '100%', height: '140px', objectFit: 'cover', borderRadius: '8px', background: '#e2e8f0' }}
                  />
                  {item.type === 'original' ? (
                    <>
                      {canEditImages ? (
                        <input
                          value={item.image.Name || ''}
                          onChange={(e) => setImages((prev) => prev.map((image) => image.ImageId === item.image.ImageId ? { ...image, Name: e.target.value } : image))}
                          onBlur={(e) => handleRename(item.image.ImageId, e.target.value)}
                          style={{ ...inputStyle, marginTop: '10px' }}
                        />
                      ) : (
                        <div style={{ marginTop: '10px', fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>{item.image.Name || 'Template Image'}</div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', fontSize: '12px', color: '#64748b', gap: '8px' }}>
                        <span>{item.image.Width || '?'} x {item.image.Height || '?'} | used {Number(item.image.UsageCount || 0)}</span>
                        {canDeleteImages && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDelete(item.image); }}
                            disabled={busyImageId === item.image.ImageId}
                            style={{ ...buttonStyle, padding: '6px 10px', color: '#b91c1c', borderColor: '#fecaca', background: '#fff1f2' }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div style={{ marginTop: '10px', display: 'grid', gap: '6px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: '#0f766e' }}>Variant</div>
                      <div style={{ fontSize: '12px', color: '#475569' }}>
                        {item.variant.Mode} | {item.variant.TargetWidth || 'auto'} x {item.variant.TargetHeight || 'auto'} | .{item.variant.OutputFormat}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        Used {Number(item.variant.UsageCount || 0)}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ background: '#fff', border: '1px solid #dbe3ef', borderRadius: '10px', padding: '16px' }}>
          <h2 style={{ marginTop: 0, fontSize: '18px' }}>Transform / Insert</h2>
          {!selectedImage ? (
            <p>Select an image from the library.</p>
          ) : (
            <>
              <img src={variantUrl || selectedImage.OriginalUrl} alt={selectedImage.Name} style={{ width: '100%', height: '220px', objectFit: 'contain', borderRadius: '8px', background: '#f1f5f9', marginBottom: '14px' }} />
              <div style={{ display: 'grid', gap: '10px' }}>
                <label>
                  <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>Mode</div>
                  <select value={transform.mode} onChange={(e) => setTransform((prev) => ({ ...prev, mode: e.target.value }))} style={inputStyle} disabled={!canEditImages}>
                    <option value="contain">Contain</option>
                    <option value="cover">Cover</option>
                    <option value="fill">Fill</option>
                    <option value="crop">Crop</option>
                  </select>
                </label>
                <label>
                  <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>Width</div>
                  <input value={transform.targetWidth} onChange={(e) => setTransform((prev) => ({ ...prev, targetWidth: e.target.value }))} style={inputStyle} disabled={!canEditImages} />
                </label>
                <label>
                  <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>Height</div>
                  <input value={transform.targetHeight} onChange={(e) => setTransform((prev) => ({ ...prev, targetHeight: e.target.value }))} style={inputStyle} disabled={!canEditImages} />
                </label>
                <label>
                  <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>Position</div>
                  <select value={transform.position} onChange={(e) => setTransform((prev) => ({ ...prev, position: e.target.value }))} style={inputStyle} disabled={!canEditImages}>
                    <option value="center">Center</option>
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                    <option value="entropy">Entropy</option>
                    <option value="attention">Attention</option>
                  </select>
                </label>
                <label>
                  <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>Output Format</div>
                  <select value={transform.outputFormat} onChange={(e) => setTransform((prev) => ({ ...prev, outputFormat: e.target.value }))} style={inputStyle} disabled={!canEditImages}>
                    <option value="png">PNG</option>
                    <option value="jpg">JPG</option>
                    <option value="webp">WEBP</option>
                  </select>
                </label>

                {!canEditImages && (
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    You have read-only access to template images.
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {canEditImages && (
                    <button type="button" onClick={() => handleTransform()} disabled={busyImageId === selectedImage.ImageId} style={{ ...buttonStyle, background: '#1d4ed8', borderColor: '#1d4ed8', color: '#fff' }}>
                      {busyImageId === selectedImage.ImageId ? 'Processing...' : 'Generate Variant'}
                    </button>
                  )}
                  {canEditImages && (
                    <button type="button" onClick={() => setCropTarget(selectedImage)} style={buttonStyle}>
                      Visual Crop
                    </button>
                  )}
                  {onSelectImage && (
                    <>
                      <button type="button" onClick={() => onSelectImage(selectedImage.OriginalUrl)} style={buttonStyle}>
                        Insert Original
                      </button>
                      {variantUrl && (
                        <button type="button" onClick={() => onSelectImage(variantUrl)} style={{ ...buttonStyle, background: '#0f766e', borderColor: '#0f766e', color: '#fff' }}>
                          Insert Variant
                        </button>
                      )}
                    </>
                  )}
                </div>

                {variantUrl && (
                  <div style={{ fontSize: '12px', color: '#334155', wordBreak: 'break-all' }}>
                    Variant URL: <a href={variantUrl} target="_blank" rel="noreferrer">{variantUrl}</a>
                  </div>
                )}

                {variants.length > 0 && (
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#334155', marginBottom: '8px' }}>Saved Variants</div>
                    <div style={{ display: 'grid', gap: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                      {variants.map((variant) => (
                        <div key={variant.VariantId} style={{ border: '1px solid #dbe3ef', borderRadius: '8px', padding: '8px', background: '#f8fafc' }}>
                          <div style={{ fontSize: '12px', color: '#334155', marginBottom: '6px' }}>{variant.Mode} | {variant.TargetWidth || 'auto'} x {variant.TargetHeight || 'auto'} | used {Number(variant.UsageCount || 0)}</div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button type="button" onClick={() => setVariantUrl(variant.VariantUrl)} style={buttonStyle}>Preview</button>
                            {onSelectImage && <button type="button" onClick={() => onSelectImage(variant.VariantUrl)} style={buttonStyle}>Insert</button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {cropTarget && (
        <CropModal
          image={cropTarget}
          onClose={() => setCropTarget(null)}
          onApply={(cropRect) => handleTransform({ ...cropRect, mode: 'crop' })}
        />
      )}
    </div>
  );
}

export default TemplateImages;
